import { useState, useEffect, useCallback, useMemo } from "react";

import { useAuth } from "@clerk/clerk-react";

import { fnUseSupabaseToken } from "../auth/auth";
import { appJotaiStore, useAtom, useAtomValue } from "../app-jotai";
import {
  bFileBrowserOpenAtom,
  sActiveDrawingIdAtom,
  sActiveDrawingNameAtom,
  sActiveFolderIdAtom,
  excalidrawAPIAtom,
} from "../cloud/cloudState";
import {
  fnListDrawings,
  fnLoadDrawing,
  fnDeleteDrawing,
  fnRenameDrawing,
  fnListFolders,
  fnCreateFolder,
  fnDeleteFolder,
  fnRenameFolder,
  fnMoveDrawing,
} from "../cloud/cloudStorage";

import type { iDrawingMetadata, iFolderMetadata } from "../cloud/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import "./FileBrowser.scss";

export const FileBrowser = () => {
  const { isSignedIn } = useAuth();
  const [bIsOpen, fnSetIsOpen] = useAtom(bFileBrowserOpenAtom);
  const oExcalidrawAPI = useAtomValue(excalidrawAPIAtom);
  const fnGetToken = fnUseSupabaseToken();

  const [aDrawings, fnSetDrawings] = useState<iDrawingMetadata[]>([]);
  const [aAllDrawings, fnSetAllDrawings] = useState<iDrawingMetadata[]>([]);
  const [aFolders, fnSetFolders] = useState<iFolderMetadata[]>([]);
  const [bLoading, fnSetLoading] = useState(false);
  const [sRenamingId, fnSetRenamingId] = useState<string | null>(null);
  const [sRenameValue, fnSetRenameValue] = useState("");
  const [sActiveFolderId, fnSetActiveFolderId] = useAtom(sActiveFolderIdAtom);
  const [sNewFolderName, fnSetNewFolderName] = useState("");
  const [bCreatingFolder, fnSetCreatingFolder] = useState(false);
  const [sFolderError, fnSetFolderError] = useState("");
  const [sRenamingFolderId, fnSetRenamingFolderId] = useState<string | null>(
    null,
  );
  const [sRenameFolderValue, fnSetRenameFolderValue] = useState("");
  const [sMovingDrawingId, fnSetMovingDrawingId] = useState<string | null>(
    null,
  );

  const fnRefreshList = useCallback(async () => {
    fnSetLoading(true);
    try {
      const sToken = await fnGetToken();
      if (!sToken) {
        console.warn("FileBrowser: no token, skipping list");
        return;
      }

      if (sActiveFolderId === null) {
        const [oFoldersResult, aUnfiledList, aAllList] = await Promise.all([
          fnListFolders(sToken),
          fnListDrawings(sToken, null),
          fnListDrawings(sToken),
        ]);
        fnSetFolders(oFoldersResult.bError ? [] : oFoldersResult.aFolders);
        fnSetDrawings(aUnfiledList);
        fnSetAllDrawings(aAllList);
      } else {
        const [oFoldersResult, aDrawingsList] = await Promise.all([
          fnListFolders(sToken),
          fnListDrawings(sToken, sActiveFolderId),
        ]);
        fnSetFolders(oFoldersResult.bError ? [] : oFoldersResult.aFolders);
        fnSetDrawings(aDrawingsList);
      }
    } catch (error) {
      console.error("FileBrowser: failed to list", error);
    } finally {
      fnSetLoading(false);
    }
  }, [fnGetToken, sActiveFolderId]);

  useEffect(() => {
    if (bIsOpen && isSignedIn) {
      fnRefreshList();
    }
  }, [bIsOpen, isSignedIn, fnRefreshList]);

  // Compute drawing counts per folder (client-side)
  const oFolderDrawingCounts = useMemo(() => {
    const oMap: Record<string, number> = {};
    for (const oDrawing of aAllDrawings) {
      if (oDrawing.folder_id) {
        oMap[oDrawing.folder_id] = (oMap[oDrawing.folder_id] || 0) + 1;
      }
    }
    return oMap;
  }, [aAllDrawings]);

  // Sort: folders by name, drawings by updated_at (already sorted from API)
  const aSortedFolders = useMemo(
    () =>
      [...aFolders].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      ),
    [aFolders],
  );

  // Find active folder name for breadcrumb
  const sActiveFolderName = useMemo(() => {
    if (sActiveFolderId === null) {
      return null;
    }
    return aFolders.find((f) => f.id === sActiveFolderId)?.name ?? "Folder";
  }, [sActiveFolderId, aFolders]);

  const fnHandleOpen = useCallback(
    async (oDrawing: iDrawingMetadata) => {
      if (!oExcalidrawAPI) {
        return;
      }

      const aCurrentElements = oExcalidrawAPI.getSceneElements();
      if (aCurrentElements.length > 0) {
        const bConfirmed = window.confirm(
          "You have unsaved local changes. Open this drawing anyway?",
        );
        if (!bConfirmed) {
          return;
        }
      }

      const sToken = await fnGetToken();
      if (!sToken) {
        oExcalidrawAPI.setToast({ message: "Not signed in." });
        return;
      }

      const oResult = await fnLoadDrawing(oDrawing.id, sToken);
      if (!oResult) {
        oExcalidrawAPI.setToast({ message: "Failed to load drawing." });
        return;
      }

      oExcalidrawAPI.updateScene({
        elements: oResult.elements,
        appState: oResult.appState as AppState,
      });

      if (Object.keys(oResult.files).length > 0) {
        oExcalidrawAPI.addFiles(Object.values(oResult.files));
      }

      appJotaiStore.set(sActiveDrawingIdAtom, oDrawing.id);
      appJotaiStore.set(sActiveDrawingNameAtom, oDrawing.name);

      fnSetIsOpen(false);
      oExcalidrawAPI.setToast({ message: `Opened "${oDrawing.name}"` });
    },
    [oExcalidrawAPI, fnGetToken, fnSetIsOpen],
  );

  const fnHandleDelete = useCallback(
    async (oDrawing: iDrawingMetadata) => {
      const bConfirmed = window.confirm(
        `Delete "${oDrawing.name}"? This cannot be undone.`,
      );
      if (!bConfirmed) {
        return;
      }

      const sToken = await fnGetToken();
      if (!sToken) {
        return;
      }

      const bSuccess = await fnDeleteDrawing(oDrawing.id, sToken);
      if (bSuccess) {
        fnSetDrawings((aPrev) => aPrev.filter((d) => d.id !== oDrawing.id));

        const sActiveId = appJotaiStore.get(sActiveDrawingIdAtom);
        if (sActiveId === oDrawing.id) {
          appJotaiStore.set(sActiveDrawingIdAtom, null);
          appJotaiStore.set(sActiveDrawingNameAtom, null);
        }
      }
    },
    [fnGetToken],
  );

  const fnHandleRename = useCallback(
    async (sDrawingId: string) => {
      if (!sRenameValue.trim()) {
        fnSetRenamingId(null);
        return;
      }

      const sToken = await fnGetToken();
      if (!sToken) {
        return;
      }

      const bSuccess = await fnRenameDrawing(
        sDrawingId,
        sRenameValue.trim(),
        sToken,
      );

      if (bSuccess) {
        fnSetDrawings((aPrev) =>
          aPrev.map((d) =>
            d.id === sDrawingId ? { ...d, name: sRenameValue.trim() } : d,
          ),
        );

        const sActiveId = appJotaiStore.get(sActiveDrawingIdAtom);
        if (sActiveId === sDrawingId) {
          appJotaiStore.set(sActiveDrawingNameAtom, sRenameValue.trim());
        }
      }

      fnSetRenamingId(null);
    },
    [sRenameValue, fnGetToken],
  );

  const fnHandleCreateFolder = useCallback(async () => {
    if (!sNewFolderName.trim()) {
      return;
    }

    fnSetFolderError("");
    const sToken = await fnGetToken();
    if (!sToken) {
      return;
    }

    const oResult = await fnCreateFolder(sNewFolderName.trim(), sToken);
    if (oResult.bSuccess) {
      fnSetNewFolderName("");
      fnSetCreatingFolder(false);
      fnRefreshList();
    } else if (oResult.sError === "duplicate") {
      fnSetFolderError("A folder with that name already exists.");
    } else {
      fnSetFolderError("Failed to create folder.");
    }
  }, [sNewFolderName, fnGetToken, fnRefreshList]);

  const fnHandleDeleteFolder = useCallback(
    async (oFolder: iFolderMetadata) => {
      const bConfirmed = window.confirm(
        `Delete folder "${oFolder.name}"? Drawings inside will be moved to root.`,
      );
      if (!bConfirmed) {
        return;
      }

      const sToken = await fnGetToken();
      if (!sToken) {
        return;
      }

      const oResult = await fnDeleteFolder(oFolder.id, sToken);
      if (oResult.bSuccess) {
        if (sActiveFolderId === oFolder.id) {
          fnSetActiveFolderId(null);
        }
        fnRefreshList();
      }
    },
    [fnGetToken, sActiveFolderId, fnSetActiveFolderId, fnRefreshList],
  );

  const fnHandleRenameFolder = useCallback(
    async (sFolderId: string) => {
      if (!sRenameFolderValue.trim()) {
        fnSetRenamingFolderId(null);
        return;
      }

      const sToken = await fnGetToken();
      if (!sToken) {
        return;
      }

      const oResult = await fnRenameFolder(
        sFolderId,
        sRenameFolderValue.trim(),
        sToken,
      );

      if (oResult.bSuccess) {
        fnSetFolders((aPrev) =>
          aPrev.map((f) =>
            f.id === sFolderId ? { ...f, name: sRenameFolderValue.trim() } : f,
          ),
        );
      } else if (oResult.sError === "duplicate") {
        fnSetFolderError("A folder with that name already exists.");
      }

      fnSetRenamingFolderId(null);
    },
    [sRenameFolderValue, fnGetToken],
  );

  const fnHandleMoveDrawing = useCallback(
    async (sDrawingId: string, sFolderId: string | null) => {
      const sToken = await fnGetToken();
      if (!sToken) {
        return;
      }

      const bSuccess = await fnMoveDrawing(sDrawingId, sFolderId, sToken);
      if (bSuccess) {
        fnSetMovingDrawingId(null);
        fnRefreshList();
      } else {
        const oAPI = appJotaiStore.get(excalidrawAPIAtom);
        if (oAPI) {
          oAPI.setToast({ message: "Failed to move drawing." });
        }
      }
    },
    [fnGetToken, fnRefreshList],
  );

  if (!bIsOpen || !isSignedIn) {
    return null;
  }

  return (
    <div className="file-browser-overlay" onClick={() => fnSetIsOpen(false)}>
      <div className="file-browser" onClick={(e) => e.stopPropagation()}>
        <div className="file-browser__header">
          <h3 className="file-browser__title">Cloud Library</h3>
          <div className="file-browser__header-actions">
            {sActiveFolderId === null && (
              <button
                className="file-browser__new-folder"
                onClick={() => fnSetCreatingFolder(true)}
                title="New Folder"
              >
                + Folder
              </button>
            )}
            <button
              className="file-browser__close"
              onClick={() => fnSetIsOpen(false)}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="file-browser__breadcrumb">
          <button
            className={`file-browser__breadcrumb-item ${
              sActiveFolderId === null
                ? "file-browser__breadcrumb-item--active"
                : ""
            }`}
            onClick={() => fnSetActiveFolderId(null)}
          >
            All Drawings
          </button>
          {sActiveFolderName && (
            <>
              <span className="file-browser__breadcrumb-sep">&gt;</span>
              <span className="file-browser__breadcrumb-item file-browser__breadcrumb-item--active">
                {sActiveFolderName}
              </span>
            </>
          )}
        </div>

        {/* New folder input */}
        {bCreatingFolder && (
          <div className="file-browser__new-folder-form">
            <input
              className="file-browser__rename-input"
              value={sNewFolderName}
              onChange={(e) => fnSetNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fnHandleCreateFolder();
                }
                if (e.key === "Escape") {
                  fnSetCreatingFolder(false);
                  fnSetFolderError("");
                }
              }}
              placeholder="Folder name"
              autoFocus
              maxLength={100}
            />
            <button onClick={fnHandleCreateFolder}>Create</button>
            <button
              onClick={() => {
                fnSetCreatingFolder(false);
                fnSetFolderError("");
              }}
            >
              Cancel
            </button>
            {sFolderError && (
              <div className="file-browser__error">{sFolderError}</div>
            )}
          </div>
        )}

        {bLoading ? (
          <div className="file-browser__loading">Loading...</div>
        ) : aDrawings.length === 0 &&
          aSortedFolders.length === 0 &&
          sActiveFolderId === null ? (
          <div className="file-browser__empty">
            No saved drawings yet. Use "Save to Cloud" to get started.
          </div>
        ) : (
          <div className="file-browser__list">
            {/* Folder rows (root view only) */}
            {sActiveFolderId === null &&
              aSortedFolders.map((oFolder) => (
                <div key={oFolder.id} className="file-browser__folder-row">
                  {sRenamingFolderId === oFolder.id ? (
                    <input
                      className="file-browser__rename-input"
                      value={sRenameFolderValue}
                      onChange={(e) => fnSetRenameFolderValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          fnHandleRenameFolder(oFolder.id);
                        }
                        if (e.key === "Escape") {
                          fnSetRenamingFolderId(null);
                        }
                      }}
                      onBlur={() => fnHandleRenameFolder(oFolder.id)}
                      autoFocus
                      maxLength={100}
                    />
                  ) : (
                    <button
                      className="file-browser__item-name file-browser__item-name--folder"
                      onClick={() => fnSetActiveFolderId(oFolder.id)}
                      title={`Open folder "${oFolder.name}"`}
                    >
                      <span className="file-browser__folder-icon">
                        &#128193;
                      </span>
                      {oFolder.name}
                      <span className="file-browser__folder-count">
                        {oFolderDrawingCounts[oFolder.id] || 0}
                      </span>
                    </button>
                  )}

                  <div className="file-browser__item-actions">
                    <button
                      title="Rename folder"
                      onClick={() => {
                        fnSetRenamingFolderId(oFolder.id);
                        fnSetRenameFolderValue(oFolder.name);
                      }}
                    >
                      &#9998;
                    </button>
                    <button
                      title="Delete folder"
                      onClick={() => fnHandleDeleteFolder(oFolder)}
                    >
                      &#128465;
                    </button>
                  </div>
                </div>
              ))}

            {/* Drawing rows */}
            {aDrawings.length === 0 && sActiveFolderId !== null ? (
              <div className="file-browser__empty">This folder is empty.</div>
            ) : (
              aDrawings.map((oDrawing) => (
                <div key={oDrawing.id} className="file-browser__item">
                  {sRenamingId === oDrawing.id ? (
                    <input
                      className="file-browser__rename-input"
                      value={sRenameValue}
                      onChange={(e) => fnSetRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          fnHandleRename(oDrawing.id);
                        }
                        if (e.key === "Escape") {
                          fnSetRenamingId(null);
                        }
                      }}
                      onBlur={() => fnHandleRename(oDrawing.id)}
                      autoFocus
                      maxLength={100}
                    />
                  ) : (
                    <button
                      className="file-browser__item-name"
                      onClick={() => fnHandleOpen(oDrawing)}
                      title={`Open "${oDrawing.name}"`}
                    >
                      {oDrawing.name}
                    </button>
                  )}

                  <span className="file-browser__item-date">
                    {new Date(oDrawing.updated_at).toLocaleDateString()}
                  </span>

                  <div className="file-browser__item-actions">
                    <button
                      title="Rename"
                      onClick={() => {
                        fnSetRenamingId(oDrawing.id);
                        fnSetRenameValue(oDrawing.name);
                      }}
                    >
                      &#9998;
                    </button>
                    {/* Move menu */}
                    <button
                      title="Move to folder"
                      onClick={() =>
                        fnSetMovingDrawingId(
                          sMovingDrawingId === oDrawing.id ? null : oDrawing.id,
                        )
                      }
                    >
                      &#128194;
                    </button>
                    <button
                      title="Delete"
                      onClick={() => fnHandleDelete(oDrawing)}
                    >
                      &#128465;
                    </button>

                    {sMovingDrawingId === oDrawing.id && (
                      <div className="file-browser__move-menu">
                        {sActiveFolderId !== null && (
                          <button
                            onClick={() =>
                              fnHandleMoveDrawing(oDrawing.id, null)
                            }
                          >
                            Remove from folder
                          </button>
                        )}
                        {aFolders.slice(0, 50).map((oFolder) =>
                          oFolder.id !== oDrawing.folder_id ? (
                            <button
                              key={oFolder.id}
                              onClick={() =>
                                fnHandleMoveDrawing(oDrawing.id, oFolder.id)
                              }
                            >
                              &#128193; {oFolder.name}
                            </button>
                          ) : null,
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};
